import React, { useEffect } from "react";
import { Box, Button, Typography } from "@mui/material";
import { currencyValues, ivaValues } from "../../constants/paymentValues";
import { fromStringToNumber } from "../../components/numberInput";
import { orderObjectStatus } from "../../constants/orderStatus";
import { formatCurrencyManual } from "../../utils/formatters";
import { FormCard, SelectInput, TextInput, TextArea } from "../../components/ui/form";
import { Num } from "../../components/ui";
import { IconPlus, IconClose } from "../../components/ui/icons";
import { tokens } from "../../theme";

/*
  Diez columnas no entran en un teléfono, así que cada detalle es una ficha en
  pantalla chica y una fila a partir de md. Tres de las columnas —N°, Alta y
  Cotización— venían como inputs deshabilitados: van como texto, que ocupa
  bastante menos y deja aire para la descripción.
*/
const COLS = "104px 72px minmax(0, 1fr) 118px 92px 104px 118px 40px";

const HeadCell = ({ children, align }) => (
  <Typography
    sx={{
      fontSize: "10.5px",
      fontWeight: 600,
      letterSpacing: "0.06em",
      textTransform: "uppercase",
      color: tokens.ink3,
      textAlign: align || "left",
    }}
  >
    {children}
  </Typography>
);

/** Etiqueta que sólo aparece en la ficha de teléfono. */
const MobileLabel = ({ children }) => (
  <Typography
    sx={{
      display: { xs: "block", md: "none" },
      fontSize: "11px",
      fontWeight: 500,
      color: tokens.ink3,
      marginBottom: "4px",
    }}
  >
    {children}
  </Typography>
);

const DetailOrder = ({
  order,
  orderDetails,
  setOrderDetails,
  setTotalExpenses,
  setTotalExpensesWithIva,
  setTotalExpensesWithIva21,
  setTotalExpensesWithIva105,
  disableEdition,
  setDeletePrevColumnsId,
}) => {
  useEffect(() => {
    const calculateTotals = () => {
      const resultWithoutIva = orderDetails.reduce((acc, obj) => {
        const quantity = fromStringToNumber(obj.quantity);
        // Si hay precio con IVA, calcular el precio sin IVA, sino usar el precio directo
        const priceWithIva = obj.priceWithIva && obj.priceWithIva !== "" 
          ? fromStringToNumber(obj.priceWithIva) 
          : null;
        const ivaPercentage = parseFloat(obj.ivaPercentage);
        const ivaMultiplier = 1 + (ivaPercentage / 100);
        const price = priceWithIva 
          ? Math.round((priceWithIva / ivaMultiplier) * 1000) / 1000  // Convertir precio con IVA a sin IVA
          : fromStringToNumber(obj.price);
        
        const rate =
          obj.currency !== "1" && obj.currency !== 1
            ? fromStringToNumber(order?.currencyRate && order.currencyRate !== "" ? order.currencyRate : "1")
            : 1;
        return acc + quantity * price * rate;
      }, 0);

      const iva21Total = orderDetails.reduce((acc, obj) => {
        if (obj.ivaPercentage === 21 || obj.ivaPercentage === "21") {
          const quantity = fromStringToNumber(obj.quantity);
          // Si hay precio con IVA, calcular el IVA desde ahí, sino usar el precio sin IVA
          const priceWithIva = obj.priceWithIva && obj.priceWithIva !== "" 
            ? fromStringToNumber(obj.priceWithIva) 
            : null;
          const ivaPercentage = parseFloat(obj.ivaPercentage);
          const ivaMultiplier = 1 + (ivaPercentage / 100);
          const price = priceWithIva 
            ? Math.round((priceWithIva / ivaMultiplier) * 1000) / 1000
            : fromStringToNumber(obj.price);
          
          const rate =
            obj.currency !== "1" && obj.currency !== 1
              ? fromStringToNumber(order?.currencyRate && order.currencyRate !== "" ? order.currencyRate : "1")
              : 1;
          return acc + quantity * price * rate * 0.21;
        }
        return acc;
      }, 0);

      const iva105Total = orderDetails.reduce((acc, obj) => {
        if (obj.ivaPercentage === 10.5 || obj.ivaPercentage === "10.5") {
          const quantity = fromStringToNumber(obj.quantity);
          // Si hay precio con IVA, calcular el IVA desde ahí, sino usar el precio sin IVA
          const priceWithIva = obj.priceWithIva && obj.priceWithIva !== "" 
            ? fromStringToNumber(obj.priceWithIva) 
            : null;
          const ivaPercentage = parseFloat(obj.ivaPercentage);
          const ivaMultiplier = 1 + (ivaPercentage / 100);
          const price = priceWithIva 
            ? Math.round((priceWithIva / ivaMultiplier) * 1000) / 1000
            : fromStringToNumber(obj.price);
          
          const rate =
            obj.currency !== "1" && obj.currency !== 1
              ? fromStringToNumber(order?.currencyRate && order.currencyRate !== "" ? order.currencyRate : "1")
              : 1;
          return acc + quantity * price * rate * 0.105;
        }
        return acc;
      }, 0);

      // Redondear todos los totales a 3 decimales para cálculos internos
      const roundedResultWithoutIva = Math.round(resultWithoutIva * 1000) / 1000;
      const roundedIva21Total = Math.round(iva21Total * 1000) / 1000;
      const roundedIva105Total = Math.round(iva105Total * 1000) / 1000;
      const roundedTotalWithIva = Math.round((roundedResultWithoutIva + roundedIva21Total + roundedIva105Total) * 1000) / 1000;

      setTotalExpenses(roundedResultWithoutIva);
      setTotalExpensesWithIva(roundedTotalWithIva);
      setTotalExpensesWithIva21(roundedIva21Total);
      setTotalExpensesWithIva105(roundedIva105Total);
    };

    calculateTotals();
  }, [orderDetails, order?.currencyRate]);



  const updateOrderDetail = (index, key, value) => {
    const updatedOrderDetails = [...orderDetails];

    updatedOrderDetails[index] = {
      ...updatedOrderDetails[index],
      [key]: value,
    };

    setOrderDetails(updatedOrderDetails);
  };

  // Función para calcular precio unitario desde precio con IVA
  const calcularPrecioUnitario = (precioConIva, ivaPercentage) => {
    const ivaMultiplier = 1 + (ivaPercentage / 100);
    return Math.round((parseFloat(precioConIva) / ivaMultiplier) * 100) / 100;
  };

  // Función para calcular precio con IVA desde precio unitario
  const calcularPrecioConIva = (precioUnitario, ivaPercentage) => {
    const ivaMultiplier = 1 + (ivaPercentage / 100);
    return Math.round((parseFloat(precioUnitario) * ivaMultiplier) * 100) / 100;
  };

  // Función para actualizar precio con IVA (recalcula precio unitario)
  const updatePriceWithIva = (index, value) => {
    const updatedOrderDetails = [...orderDetails];
    
    updatedOrderDetails[index] = {
      ...updatedOrderDetails[index],
      priceWithIva: value,
    };

    // Si hay valor válido, recalcular precio unitario
    if (value && !isNaN(parseFloat(value))) {
      const ivaPercentage = parseFloat(updatedOrderDetails[index].ivaPercentage);
      const nuevoPrecioUnitario = calcularPrecioUnitario(value, ivaPercentage);
      updatedOrderDetails[index].price = nuevoPrecioUnitario.toFixed(2);
    }

    setOrderDetails(updatedOrderDetails);
  };

  // Función para actualizar precio unitario (recalcula precio con IVA)
  const updatePrice = (index, value) => {
    const updatedOrderDetails = [...orderDetails];
    
    updatedOrderDetails[index] = {
      ...updatedOrderDetails[index],
      price: value,
    };

    // Si hay valor válido, recalcular precio con IVA
    if (value && !isNaN(parseFloat(value))) {
      const ivaPercentage = parseFloat(updatedOrderDetails[index].ivaPercentage);
      const nuevoPrecioConIva = calcularPrecioConIva(value, ivaPercentage);
      updatedOrderDetails[index].priceWithIva = nuevoPrecioConIva.toFixed(2);
    }

    setOrderDetails(updatedOrderDetails);
  };

  // Función para actualizar IVA (usa precio unitario como base)
  const updateIvaPercentage = (index, value) => {
    const updatedOrderDetails = [...orderDetails];
    
    updatedOrderDetails[index] = {
      ...updatedOrderDetails[index],
      ivaPercentage: value,
    };

    // Solo recalcular si hay precio unitario
    if (updatedOrderDetails[index].price && updatedOrderDetails[index].price !== "" && !isNaN(parseFloat(updatedOrderDetails[index].price))) {
      const ivaPercentage = parseFloat(value);
      const nuevoPrecioConIva = calcularPrecioConIva(updatedOrderDetails[index].price, ivaPercentage);
      updatedOrderDetails[index].priceWithIva = nuevoPrecioConIva.toFixed(2);
    }

    setOrderDetails(updatedOrderDetails);
  };

  const addDetailsColumn = () => {
    if (!disableEdition) {
      const newColumn = {
        createdAt: new Date(),
        currency: 1,
        description: "",
        doneAt: null,
        ivaPercentage: 21,
        orderDetailsId: "",
        price: "",
        priceWithIva: "",
        quantity: 1,
        allowDelete: true,
      };

      setOrderDetails([...orderDetails, newColumn]);
    }
  };

  const deleteColumn = (index) => {
    if (!disableEdition) {
      const newArray = [...orderDetails];
      newArray.splice(index, 1);
      setOrderDetails(newArray);
    }
  };

  const puedeEditar = order.status !== orderObjectStatus.cancelado && !disableEdition;

  return (
    <FormCard
      title="Detalles"
      subtitle={`${orderDetails?.length || 0} ${orderDetails?.length === 1 ? "ítem" : "ítems"}`}
      action={
        puedeEditar && (
          <Button
            variant="outlined"
            startIcon={<IconPlus size={15} />}
            onClick={addDetailsColumn}
            sx={{ minHeight: 36, flexShrink: 0 }}
          >
            Agregar
          </Button>
        )
      }
      sx={{ width: "100%", minWidth: 0 }}
    >
      {/* Encabezado sólo en escritorio: en teléfono cada ítem lleva sus etiquetas. */}
      <Box
        sx={{
          display: { xs: "none", md: "grid" },
          gridTemplateColumns: COLS,
          gap: "8px",
          padding: "0 12px 6px",
          borderBottom: `1px solid ${tokens.border}`,
        }}
      >
        <HeadCell>Detalle</HeadCell>
        <HeadCell>Cant.</HeadCell>
        <HeadCell>Descripción</HeadCell>
        <HeadCell align="right">P. Unit.</HeadCell>
        <HeadCell>IVA</HeadCell>
        <HeadCell>Moneda</HeadCell>
        <HeadCell align="right">Sub total</HeadCell>
        <HeadCell />
      </Box>

      <Box sx={{ display: "flex", flexDirection: "column", gap: { xs: "10px", md: "6px" } }}>
        {orderDetails?.map((dataItem, index) => {
          // Para el subtotal usar el precio con IVA si está disponible, sino calcularlo
          let priceToUse;
          if (dataItem.priceWithIva && dataItem.priceWithIva !== "") {
            priceToUse = fromStringToNumber(dataItem.priceWithIva);
          } else {
            // Calcular precio con IVA basado en el precio unitario y el IVA seleccionado
            const priceWithoutIva = fromStringToNumber(dataItem.price);
            const ivaPercentage = parseFloat(dataItem.ivaPercentage);
            const ivaMultiplier = 1 + (ivaPercentage / 100);
            priceToUse = priceWithoutIva * ivaMultiplier;
          }

          // Obtener la cotización, usar 1 si no está definida o es inválida
          const currencyRateValue = (dataItem.currency !== 1 && dataItem.currency !== "1")
            ? (order?.currencyRate && order.currencyRate !== "" ? order.currencyRate : "1")
            : "1";

          const subTotal =
            fromStringToNumber(dataItem.quantity) *
            priceToUse *
            fromStringToNumber(currencyRateValue);
          let subtotalFixed = Math.round(subTotal * 1000) / 1000;

          const esDolar = dataItem.currency !== 1 && dataItem.currency !== "1";
          const alta = dataItem.createdAt ? new Date(dataItem.createdAt).toLocaleDateString() : "—";

          return (
            <Box
              key={index}
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "minmax(0, 1fr)", md: COLS },
                gap: { xs: "10px", md: "8px" },
                alignItems: { xs: "stretch", md: "start" },
                padding: { xs: "12px", md: "8px 12px" },
                borderRadius: `${tokens.radiusControl}px`,
                border: `1px solid ${tokens.border}`,
                backgroundColor: { xs: tokens.surface2, md: "transparent" },
                minWidth: 0,
              }}
            >
              {/* N° y alta: sólo lectura, así que van como texto. */}
              <Box
                sx={{
                  display: "flex",
                  flexDirection: { xs: "row", md: "column" },
                  alignItems: { xs: "center", md: "flex-start" },
                  justifyContent: "space-between",
                  gap: "6px",
                  minWidth: 0,
                  paddingTop: { md: "9px" },
                }}
              >
                <Box sx={{ display: "flex", alignItems: "baseline", gap: "7px", minWidth: 0 }}>
                  <Num sx={{ fontSize: "13px", fontWeight: 600 }}>
                    {dataItem.orderDetailsId ? `#${dataItem.orderDetailsId}` : "Nuevo"}
                  </Num>
                  <Num sx={{ fontSize: "11.5px", color: tokens.ink3, whiteSpace: "nowrap" }}>
                    {alta}
                  </Num>
                </Box>

                {/* En teléfono el borrar va acá arriba, al alcance del pulgar. */}
                {puedeEditar && (
                  <Box
                    component="button"
                    type="button"
                    aria-label="Eliminar detalle"
                    onClick={() => {
                      deleteColumn(index);
                      if (!dataItem.allowDelete) {
                        setDeletePrevColumnsId((prevState) => {
                          return [...prevState, dataItem?.orderDetailsId];
                        });
                      }
                    }}
                    sx={{
                      display: { xs: "flex", md: "none" },
                      alignItems: "center",
                      justifyContent: "center",
                      width: 32,
                      height: 32,
                      flexShrink: 0,
                      padding: 0,
                      borderRadius: `${tokens.radiusControl}px`,
                      border: `1px solid ${tokens.border}`,
                      backgroundColor: tokens.surface,
                      color: tokens.ink3,
                      cursor: "pointer",
                      "&:hover": { borderColor: tokens.brandBorder, color: tokens.brandDark },
                    }}
                  >
                    <IconClose size={15} />
                  </Box>
                )}
              </Box>

              <Box sx={{ minWidth: 0 }}>
                <MobileLabel>Cantidad</MobileLabel>
                <TextInput
                  type="text"
                  inputMode="decimal"
                  numeric
                  disabled={disableEdition}
                  value={dataItem.quantity}
                  onChange={(e) => {
                    const value = e.target.value.replace(/[^0-9.]/g, "");
                    updateOrderDetail(index, "quantity", value);
                  }}
                  sx={{ textAlign: "right" }}
                />
              </Box>

              <Box sx={{ minWidth: 0 }}>
                <MobileLabel>Descripción</MobileLabel>
                <TextArea
                  disabled={disableEdition}
                  value={dataItem.description}
                  onChange={(e) => updateOrderDetail(index, "description", e.target.value)}
                  rows={2}
                  sx={{ minHeight: 40 }}
                />
              </Box>

              <Box sx={{ minWidth: 0 }}>
                <MobileLabel>Precio unitario</MobileLabel>
                <TextInput
                  type="text"
                  inputMode="decimal"
                  numeric
                  disabled={disableEdition}
                  value={dataItem.price}
                  onChange={(e) => {
                    let value = e.target.value.replace(/[^0-9.]/g, "");
                    // Asegura que solo haya un punto decimal
                    const parts = value.split(".");
                    if (parts.length > 2) {
                      value = parts[0] + "." + parts.slice(1).join("");
                    }
                    updatePrice(index, value);
                  }}
                  sx={{ textAlign: "right" }}
                />
              </Box>

              <Box sx={{ minWidth: 0 }}>
                <MobileLabel>IVA</MobileLabel>
                <SelectInput
                  disabled={disableEdition}
                  value={dataItem.ivaPercentage}
                  onChange={(e) => updateIvaPercentage(index, e.target.value)}
                  sx={{ paddingLeft: "9px", paddingRight: "26px" }}
                >
                  {ivaValues.map((item) => (
                    <option key={item.value} value={item.value}>
                      {item.label}
                    </option>
                  ))}
                </SelectInput>
              </Box>

              <Box sx={{ minWidth: 0 }}>
                <MobileLabel>Moneda</MobileLabel>
                <SelectInput
                  disabled={disableEdition}
                  value={dataItem.currency}
                  onChange={(e) => updateOrderDetail(index, "currency", e.target.value)}
                  sx={{ paddingLeft: "9px", paddingRight: "26px" }}
                >
                  {currencyValues.map((item) => (
                    <option key={item.value} value={item.value}>
                      {item.label}
                    </option>
                  ))}
                </SelectInput>
                {/* La cotización es de sólo lectura y sólo dice algo en moneda extranjera. */}
                {esDolar && (
                  <Typography sx={{ fontSize: "11px", color: tokens.ink3, marginTop: "4px" }}>
                    Cotización {order.currencyRate || "—"}
                  </Typography>
                )}
              </Box>

              <Box
                sx={{
                  display: "flex",
                  flexDirection: { xs: "row", md: "column" },
                  alignItems: { xs: "baseline", md: "flex-end" },
                  justifyContent: "space-between",
                  gap: "6px",
                  minWidth: 0,
                  paddingTop: "9px",
                  borderTop: { xs: `1px solid ${tokens.border}`, md: "none" },
                }}
              >
                <MobileLabel>Sub total</MobileLabel>
                <Num sx={{ fontSize: { xs: "15px", md: "13.5px" }, fontWeight: 600 }}>
                  {formatCurrencyManual(subtotalFixed)}
                </Num>
              </Box>

              {/* En escritorio el borrar va al final de la fila. */}
              <Box sx={{ display: { xs: "none", md: "flex" }, justifyContent: "center", paddingTop: "9px" }}>
                {puedeEditar && (
                  <Box
                    component="button"
                    type="button"
                    aria-label="Eliminar detalle"
                    onClick={() => {
                      deleteColumn(index);
                      if (!dataItem.allowDelete) {
                        setDeletePrevColumnsId((prevState) => {
                          return [...prevState, dataItem?.orderDetailsId];
                        });
                      }
                    }}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      width: 30,
                      height: 30,
                      padding: 0,
                      borderRadius: "7px",
                      border: "none",
                      backgroundColor: "transparent",
                      color: tokens.ink3,
                      cursor: "pointer",
                      "&:hover": { backgroundColor: tokens.brandTint, color: tokens.brandDark },
                    }}
                  >
                    <IconClose size={15} />
                  </Box>
                )}
              </Box>
            </Box>
          );
        })}
      </Box>
    </FormCard>
  );
};

export default DetailOrder;
